import  React from 'react'
import { Alert } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import * as H from 'history'
import { GrantAgreementModel } from '../../../models/GrantAgreementModel'
import { ISupportSpinner } from '../../../shared/Ajax'
import { Backdrop } from '../../../shared/Backdrop'
import {
  Loading, NumericSelectField, TextField, validateFields,
} from '../../../shared/fields'
import { Salutation } from '../../../shared/Utility'
import { ApplicationController } from '../../../controllers/ApplicationController'
import { DashboardModel } from '../../../models/DashboardModel'
import ApplicationSummary from './ApplicationSummary'

interface Props {
  id: string
  history: H.History
}

export interface State {
    loading: boolean
    grantAgreementModel: GrantAgreementModel
    model: DashboardModel
    showGivenNameError: boolean
    showFamilyNameError: boolean
    disableSubmit: boolean
}

export default class DeclineForm extends React.Component<Props, State> implements ISupportSpinner {
  title: NumericSelectField
  givenName: TextField
  familyName: TextField
  position: TextField
  ajax: ApplicationController

  constructor(props: Props) {
    super(props)
    this.ajax = new ApplicationController()
    this.state = {
      loading: false,
      model: null,
      grantAgreementModel: null,
      showGivenNameError: null,
      showFamilyNameError: null,
      disableSubmit: false,
    }
  }

  componentDidMount() {
    const { id, history } = this.props
    this.ajax.getDashBoardDetailByApplicationId(this, id, (model: DashboardModel) => {
      const userAuthToView = model?.dashboardDetailModels[0]?.grantAgreementSummaryModel?.canAcceptOrDecline

      if (!userAuthToView) {
        history.push('/') // previously '/dashboard-reform'
      }

      this.setState({
        model,
        grantAgreementModel: {
          ...model?.dashboardDetailModels?.[0].grantAgreementModel,
          claimRandomIdentifier: model.dashboardDetailModels[0].claimRandomIdentifier,
        },
      })
    }, (err) => {
      alert(err)
      history.push('/') // previously '/dashboard-reform'
    })
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    const { grantAgreementModel } = this.state

    if (grantAgreementModel?.givenName !== prevState?.grantAgreementModel?.givenName) {
      this.setState({ showGivenNameError: false })
    }

    if (grantAgreementModel?.familyName !== prevState?.grantAgreementModel?.familyName) {
      this.setState({ showFamilyNameError: false })
    }
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  onChange = (changes: Partial<GrantAgreementModel>, callback?: { (): any }) => {
    this.setState({ grantAgreementModel: { ...this.state.grantAgreementModel, ...changes } }, callback)
  }

  downloadAgreement() {
    const { model } = this.state
    const claimIdentifier = model?.dashboardDetailModels?.[0]?.claimRandomIdentifier
    window.open(`/api/DocumentReform/downloadAgreement?claimIdentifier=${claimIdentifier}`, '_blank')
  }

  getGivenNameError() {
    const { grantAgreementModel, model } = this.state
    return model?.dashboardDetailModels[0]?.primaryContactGivenName?.toLowerCase().trim() !== grantAgreementModel?.givenName?.toLowerCase()
  }

  getFamilyNameError() {
    const { grantAgreementModel, model } = this.state
    return model?.dashboardDetailModels[0]?.primaryContactFamilyName?.toLowerCase().trim() !== grantAgreementModel?.familyName?.toLowerCase()
  }

  canSubmit(): Promise<boolean> {
    return new Promise((resolve) => {
      const { model } = this.state
      const { id } = this.props
      this.ajax.getDashBoardDetailByApplicationId(this, id, (newModel: DashboardModel) => {
        const newGrantAgreementSummaryModel = newModel.dashboardDetailModels[0].grantAgreementSummaryModel
          const stateGrantAgreementSummaryModel = model.dashboardDetailModels[0].grantAgreementSummaryModel

          // Parse the dueDate
          const dueDate = new Date(newGrantAgreementSummaryModel.dueDate);
          const currentDate = new Date();

          // Check if the dueDate has elapsed
          if (dueDate < currentDate) {
              resolve(false);
              return;
          }
        resolve(Object.entries(stateGrantAgreementSummaryModel).sort().toString() === Object.entries(newGrantAgreementSummaryModel).sort().toString())
      })
    })
  }

  validate() {
    validateFields((res) => {
      this.setState({ showFamilyNameError: this.getFamilyNameError(), showGivenNameError: this.getGivenNameError() }, async () => {
        if (!this.getFamilyNameError() && !this.getGivenNameError() && res) {
          if (confirm('You are about to decline your acceptance of the grant agreement. Proceed? \n\n You can\'t make any changes once you decline your grant agreem​ent.')) {
            const { grantAgreementModel } = this.state
            const canSubmit = await this.canSubmit()
            if (canSubmit) {
              this.ajax.grantAgreementDeclined(this, grantAgreementModel,
                (grantAgreementNumber: string) => this.setState({ grantAgreementModel: { ...grantAgreementModel, grantAgreementNumber } }))
            } else {
              alert('There has been an update to the grant agreement details. Please refresh the page')
              this.setState({ disableSubmit: true })
            }
          }
        }
      })
    },
    this.title,
    this.givenName,
    this.familyName,
    this.position)
  }

  getErrorMessage(field: string) {
    const {
      grantAgreementModel, model, showGivenNameError, showFamilyNameError,
    } = this.state
    const applicationDetails = model?.dashboardDetailModels?.[0]
    if (field === 'givenName' && showGivenNameError) {
      if (grantAgreementModel.givenName?.length > 0 && applicationDetails.primaryContactGivenName !== grantAgreementModel.givenName) {
        return 'The  Applicant Given Name is incorrect. It must be the same Given Name as recorded for your application\'s primary contact.'
      }
    }

    if (field === 'familyName' && showFamilyNameError) {
      if (grantAgreementModel.familyName?.length > 0 && applicationDetails.primaryContactFamilyName !== grantAgreementModel.familyName) {
        return 'The  Applicant Family Name is incorrect. It must be the same Family Name as recorded for your application\'s primary contact.'
      }
    }
    return null
  }

  render() {
    const {
      grantAgreementModel, loading, model, disableSubmit,
    } = this.state

    const { history } = this.props

    if (!model) return <Loading />

    const applicationDetails = model?.dashboardDetailModels?.[0]
    const isDraft = applicationDetails.applicationStatus.toLowerCase() === 'draft'

    const salutationDataSource = [
      { value: Salutation.Mr, label: 'Mr' },
      { value: Salutation.Mrs, label: 'Mrs' },
      { value: Salutation.Ms, label: 'Ms' },
      { value: Salutation.Miss, label: 'Miss' },
      { value: Salutation.Other, label: 'Other' },
    ]

    if (grantAgreementModel?.grantAgreementNumber) {
      return (
        <div className='container-fluid pt-3'>
          <h2>Decline Grant Agreement - Confirmation</h2>
          <br />
          <ApplicationSummary
            isConsultant={model.isConsultant}
            isDraft={isDraft}
            model={model}
            applicationDetails={applicationDetails}
            showGrantAgreement
            grantAgreement={applicationDetails.grantAgreementSummaryModel}
          />
          <br />
          <div>Your request to decline your agreement has been received.</div>
          <br />
          <div>
            For help, you can contact the EMDG helpline 13 28 78 or email
            {' '}
            <a href="mailto: EMDG.help@austrade.gov.au">EMDG.help@austrade.gov.au.</a>
          </div>
          <br />
          <div className="row">
            <div className="col-sm-8 mb-1">
              <Button className="emdg-btn emdg-btn--secondary mr-2" id="btnViewAgreement" onClick={() => history.goBack()}>
                Back
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className='container-fluid pt-3'>
        <h2>Decline Grant Agreement</h2>
        <br />
        <ApplicationSummary
          isConsultant={model.isConsultant}
          isDraft={isDraft}
          model={model}
          applicationDetails={applicationDetails}
          showGrantAgreement
          grantAgreement={applicationDetails.grantAgreementSummaryModel}
        />
        <br />
        <div style={{ width: '80%' }}>
          <Alert className="emdg-page-alerts">
            <>
              To discuss your agreement or request changes, please contact your Austrade grant assessor before declining the agreement.
              Alternatively you can contact the EMDG helpline 13 28 78 or you can email EMDG.help@austrade.gov.au.
            </>
          </Alert>

          <NumericSelectField
            ref={(r) => { this.title = r }}
            id="title"
            label="Applicant Title"
            value={grantAgreementModel.title}
            onChange={(title) => this.onChange({ title })}
            dataSource={salutationDataSource}
            block={false}
            required
          />

          <TextField
            ref={(r) => { this.givenName = r }}
            id="givenName"
            label="Applicant Given name"
            value={grantAgreementModel.givenName}
            onChange={(givenName) => this.onChange({ givenName: givenName.trim() })}
            required
            maxLength={50}
            errorMessage={this.getErrorMessage('givenName')}
          />

          <TextField
            ref={(r) => { this.familyName = r }}
            id="familyName"
            label="Applicant Family name"
            value={grantAgreementModel.familyName}
            onChange={(familyName) => this.onChange({ familyName: familyName.trim() })}
            required
            maxLength={50}
            errorMessage={this.getErrorMessage('familyName')}
          />

          <TextField
            ref={(r) => { this.position = r }}
            id="position"
            label="Applicant Position Title"
            value={grantAgreementModel.position}
            onChange={(position) => this.onChange({ position })}
            required
            maxLength={50}
          />
          <br />
          <div className="row">
            <div className="col-sm-8 mb-1">
              <Button className="emdg-btn emdg-btn--secondary mr-2" id="btnViewAgreement" onClick={() => history.goBack()}>
                Back
              </Button>
              <Button className="emdg-btn emdg-btn--secondary" id="btnDeclineAgreement" onClick={() => this.downloadAgreement()}>
                View Grant Agreement
              </Button>

            </div>
            <div className="col-sm-4 mb-1">
              <div className="float-sm-right">
                <Button className="emdg-btn emdg-btn--secondary" disabled={disableSubmit} id="btnAcceptAgreement" onClick={() => this.validate()}>
                  Decline and Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Backdrop on={loading} />
      </div>
    )
  }
}
